import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { ProgressBagEvents } from '@app/shared/modals/live-ops/progressbag/progressbag-events.service';
import { LimitedTimeEvent, PointsPerBagMultiplier } from '@app/shared/models/limitedTimeEvent.model';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { TooltipBubbleComponent } from '@app/shared/components/tooltip-bubble/tooltip-bubble.component';
import { SharedModule } from '@app/shared/shared.module';

@Component({
  selector: 'slotcom-progressbag-progress-bar',
  templateUrl: './progressbag-progress-bar.component.html',
  styleUrls: ['./progressbag-progress-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TooltipBubbleComponent, SharedModule],
})
@TakeUntilDestroy
export class ProgressBagProgressBarComponent implements OnInit, AfterViewInit, OnChanges {
  private elRef = inject(ElementRef);
  private progressBagEvents = inject(ProgressBagEvents);

  @Input() animated: boolean;
  @Input() totalProgress: number = 0;
  @Input() currentProgress: number = 0;
  @Input() stepIcon: { activeAsset: string; inactiveAsset: string };
  @Input() finalIcon: { activeAsset: string; inactiveAsset: string };
  @Input() event: LimitedTimeEvent;
  @Input() pointsPerBagMultiplier: PointsPerBagMultiplier[];

  firstLoad = signal<boolean>(true);
  currentStep = signal<number>(0);
  animationsAmount = signal<number>(0);

  iconStates = signal<boolean[]>([]);
  iconStateAssets = signal<string[]>([]);

  ngOnInit(): void {
    this.getIconsStates();
  }

  ngAfterViewInit(): void {
    if (this.getCompletedRanges() === 0) {
      this.progressBagEvents.milestoneAchieved(0);
    }

    this.setElementsPosition();
    this.updateIconAssets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.currentStep() !== this.getCompletedRanges()) {
      this.getIconsStates();
      this.updateIconAssets();

      if (this.animated) {
        this.animateAsset(this.currentStep());
      } else {
        this.showAsset(this.currentStep());
      }
    }

    const progressBars: HTMLElement[] = this.elRef.nativeElement.getElementsByClassName('c-progress__fill');

    for (const progressBar of progressBars) {
      if (!this.firstLoad() && this.animated) {
        progressBar.animate(
          [
            {
              width: `${(changes.currentProgress.previousValue * 100) / this.totalProgress}%`,
            },
            {
              width: `${(changes.currentProgress.currentValue * 100) / this.totalProgress}%`,
            },
          ],
          {
            duration: 1000,
            iterations: 1,
          }
        );
      }

      const w = this.getProgressWidth();
      progressBar.style.width = `${w}%`;
      this.firstLoad.set(false);
    }
  }

  getCompletedRanges(): number {
    const steps = this.event.properties.pointsperbagmultiplier
      .filter((p) => this.currentProgress >= p.points)
      .map((r) => r.points);

    const stepsCompleted = steps ? steps.length - 1 : 0;
    this.currentStep.set(stepsCompleted);

    return stepsCompleted;
  }

  showAsset(stepsCompleted: number): void {
    this.progressBagEvents.milestoneAchieved(stepsCompleted);
  }

  animateAsset(stepsCompleted: number): void {
    this.animationsAmount.update((v) => v + 1);

    this.progressBagEvents.milestoneAchieved(this.animationsAmount());

    if (stepsCompleted > 0 && this.animationsAmount() < stepsCompleted) {
      setTimeout(() => {
        this.animateAsset(stepsCompleted);
      }, 1000);
    }
  }

  updateIconAssets(): void {
    const assets: string[] = [];

    this.iconStates().forEach((state, index) => {
      if (index < this.pointsPerBagMultiplier.length - 1) {
        if (state) {
          assets.push(this.stepIcon.activeAsset);
        } else {
          assets.push(this.stepIcon.inactiveAsset);
        }
      } else {
        if (state) {
          assets.push(this.finalIcon.activeAsset);
        } else {
          assets.push(this.finalIcon.inactiveAsset);
        }
      }
    });

    this.iconStateAssets.set(assets);
  }

  private getIconsStates(): void {
    const states: boolean[] = [];

    this.pointsPerBagMultiplier.forEach((_, index) => {
      states.push(index === this.currentStep());
    });

    this.iconStates.set(states);
  }

  private getProgressWidth(): number {
    return Number(`${(this.currentProgress * 100) / this.totalProgress}`);
  }

  private setElementsPosition(): void {
    const intervals: HTMLElement[] = this.elRef.nativeElement.getElementsByClassName('interval');
    const squares: HTMLElement[] = this.elRef.nativeElement.getElementsByClassName('square');

    const icons: HTMLElement[] = this.elRef.nativeElement.getElementsByClassName('item-icon');
    const points: HTMLElement[] = this.elRef.nativeElement.getElementsByClassName('item-points');

    const squareWidth: number = 4.2;
    const squareHeight: number = 10;
    const widthProportion: number = (squareWidth / 42) * 100;

    const squarePositions: number[] = [];

    Array.from(intervals).forEach((interval, i) => {
      if (i > 0) {
        const width = Number(
          `${
            ((this.pointsPerBagMultiplier[i].points - this.pointsPerBagMultiplier[i - 1].points) / this.totalProgress) *
            100
          }`
        );

        interval.style.width = `${width}%`;

        squares[i].style.width = `calc(${squareWidth}em * 0.757)`;
        squares[i].style.height = `calc(${squareHeight}em * 0.757)`;

        icons[i].style.width = `calc(${squareWidth}em * 0.757)`;
        points[i].style.width = `calc(${squareWidth}em * 0.757)`;

        if (i === 1) {
          const x = width - widthProportion / 2;
          squarePositions.push(x);
          squares[i].style.left = `${x}%`;
        } else {
          const x = width - widthProportion + squarePositions[i - 2];
          squarePositions.push(x);
          squares[i].style.left = `${x}%`;
        }
      } else {
        interval.style.width = '0';
        squares[0].style.display = 'none';
      }
    });
  }
}
